import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useAtomValue } from 'jotai'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { userAtom } from '@/store/store'
import useRequestMutation from '@/hooks/requests/useRequestMutation'
import useSemestersList from '@/hooks/semesters/useSemestersList'

const formSchema = z.object({
    courseCode: z.string().regex(/^[A-Za-z]{3}\d{3}$/, {
        message: 'Course code must be 3 letters followed by 3 digits (e.g., TES123).',
    }),
    section: z.string().regex(/^[A-Za-z0-9]{3,4}$/, {
        message: 'Section must be 3-4 alphanumeric characters (e.g., NAA).',
    }),
    semester: z.string().min(1, {
        message: 'Semester is required.',
    }),
})

export function RequestCourseForm() {
    const user = useAtomValue(userAtom)
    const { data: semestersList } = useSemestersList()
    const { addRequest } = useRequestMutation()

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            courseCode: '',
            section: '',
            semester: '',
        },
        mode: 'onTouched',
    })

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        addRequest.mutate({
            course_code: values.courseCode.toUpperCase(),
            section: values.section.toUpperCase(),
            semester: values.semester,
            requested_by: user.userId,
        })
        form.reset()
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 max-w-md mx-auto p-6 border rounded-lg shadow-sm bg-card">
                <h2 className="text-lg font-semibold mb-4">Request a New Course</h2>

                <FormField
                    control={form.control}
                    name="courseCode"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Course Code</FormLabel>
                            <FormControl>
                                <Input placeholder="e.g. TES123" {...field} maxLength={6} />
                            </FormControl>
                            <FormDescription>3 letters followed by 3 digits.</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="section"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Section</FormLabel>
                            <FormControl>
                                <Input placeholder="e.g. NAA" {...field} maxLength={4} />
                            </FormControl>
                            <FormDescription>3-4 alphanumeric characters.</FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="semester"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Semester</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a semester" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {semestersList?.map((semester) => (
                                        <SelectItem key={semester.code} value={semester.code} className="dark:focus:text-white">
                                            {semester.friendlyName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <Button type="submit" className="w-full" disabled={addRequest.isPending}>
                    {addRequest.isPending ? 'Submitting...' : 'Submit Request'}
                </Button>
            </form>
        </Form>
    )
}
